import { Alert, Button, Container, Snackbar, Stack, Typography } from "@mui/material";
import moment from "moment";
import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import { useRef } from "react";
import { createBlobInContainer } from "src/utils/azureBlob";
import { del, get, post, put } from "src/utils/networkUtils";
import { useLocation } from "react-router-dom";
import Iconify from "src/components/iconify";
import PrizeDrawForm from "src/components/prize-draw-admin/PrizeDrawForm";

const dateFormat = "YYYY-MM-DD HH:mm:ss";

export default function PrizeDrawAddPage() {
  const fileInputRef = useRef(null);
  const location = useLocation();
  const prizeDrawParams = location.state?.prizeDraw;
  const [loadingDeletePrizeIds, setLoadingDeletePrizeIds] = useState([]);
  const [prizeToggle, setPrizeToggle] = useState(false);
  const [prizeLoading, setPrizeLoading] = useState(false);
  const [alertState, setAlertState] = useState({
    open: false,
    message: "",
    severity: "error",
  });
  const [prizeDrawBody, setPrizeDrawBody] = useState({
    title_en: "",
    title_fr: "",
    sub_title_en: "",
    sub_title_fr: "",
    image_en: "",
    maxTicket: 0,
    startDate: moment().format(dateFormat),
    closeDate: moment().format(dateFormat),
    prizes: [],
    winnersMessage: "",
    status: 0,
  });
  const postalEntryRef = useRef(null);
  const tAndCsRef = useRef(null);
  const winnersMsgRef = useRef(null);

  useEffect(() => {
    console.log('raffleId', prizeDrawParams)
    if (prizeDrawParams) {
      get(`/admin/raffles/${prizeDrawParams.raffleId}`).then(function (
        response
      ) {
        const resp = response;
        if (resp !== null && resp !== undefined) {
          const updated = {
            ...resp,
            sub_title_en: resp.sub_title_en || "",
            sub_title_fr: resp.sub_title_fr || "",
            maxTicket:
              resp.maxTicket === -1 ? "" : resp.maxTicket,
            prizes: resp?.prizes?.map((item) => {
              return { 
                id: item.prizeId, 
                title_en: item.title_en, 
                title_fr: item.title_fr, 
                description_en: item.description_en, 
                description_fr: item.description_fr, 
                tickets: item.tickets 
              };
            }),
          };
          setPrizeDrawBody(updated);
        }
      });
    }
  }, [prizeToggle, prizeDrawParams]);

  useEffect(() => {
    if (prizeDrawParams) {
      const updated = {
        ...prizeDrawParams,
        sub_title_en: prizeDrawParams.sub_title_en || "",
        sub_title_fr: prizeDrawParams.sub_title_fr || "",
        maxTicket:
          prizeDrawParams.maxTicket === -1
            ? ""
            : prizeDrawParams.maxTicket,
        prizes: prizeDrawParams?.prizes?.map((item) => {
          return { 
            id: item.prizeId, 
            title_en: item.title_en, 
            title_fr: item.title_fr, 
            description_en: item.description_en, 
            description_fr: item.description_fr, 
            tickets: item.tickets 
          };
        }),
      };
      setPrizeDrawBody(updated);
    }
  }, [prizeDrawParams]);

  useEffect(() => {
    if (prizeDrawParams) return;
    get(`/admin/prize-draws-details`)
      .then(function (response) {
        setPrizeDrawBody((prev) => ({
          ...prev,
          postalEntry: response.prizeDrawDetails.postalEntry,
          tAndCs: response.prizeDrawDetails.tAndCs,
          winnersMessage: response.prizeDrawDetails.winnersMessage,
        }));
      })
      .catch(function (error) {
        console.error("Error fetching prize draw details:", error);
      });
  }, [prizeDrawParams]);

  const handleButtonClick = () => {
    fileInputRef.current?.click(); // triggers file input dialog
  };

  const handleChangeStatus = (event) => {
    setPrizeDrawBody((prevState) => ({
      ...prevState,
      status: event.target.value,
    }));
  };

  const handlePrizeChange = (value, prizeId, field) => {
    setPrizeDrawBody((prev) => ({
      ...prev,
      prizes: prev.prizes.map((item) =>
        item.id === prizeId ? { ...item, [field]: value } : item
      ),
    }));
  };

  const handleRemovePrizeRow = async (prizeId) => {
    const idString = prizeId.toString();
    const isNewAdded = idString.includes("temp-");
    if (isNewAdded) {
      setPrizeDrawBody((prev) => ({
        ...prev,
        prizes: prev.prizes.filter((item) => item.id !== prizeId),
      }));
    }
    if (!isNewAdded) {
      setLoadingDeletePrizeIds((prev) => [...prev, prizeId]);
      try {
        const resp = await del(
          `/admin/raffles/${prizeDrawParams.raffleId}/prize/${prizeId}`
        );
        if (resp.success) {
          setPrizeToggle(!prizeToggle);
        }
      } catch (error) {
        setLoadingDeletePrizeIds((prev) => prev.filter((id) => id !== prizeId));
      } finally {
        setLoadingDeletePrizeIds((prev) => prev.filter((id) => id !== prizeId));
      }
    }
  };

  const handleAddPrizeRow = () => {
    setPrizeDrawBody((prev) => ({
      ...prev,
      prizes: [...prev.prizes, { 
        id: `temp-${Date.now()}`,
        title_en: "",
        title_fr: "",
        description_en: "",
        description_fr: "",
        tickets: 0
      }],
    }));
  };
  const handleDateChange = (newValue, fieldName) => {
    setPrizeDrawBody((prevState) => ({
      ...prevState,
      [fieldName]: moment(newValue).format(dateFormat),
    }));
  };
  const uploadFiles = (event) => {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
      const maxSize = 2 * 1024 * 1024;

      if (!validTypes.includes(file.type)) {
        setAlertState({
          open: true,
          message: "Only JPG, JPEG, PNG, and WEBP formats are allowed.",
          severity: "error",
        });
        return;
      }

      if (file.size > maxSize) {
        setAlertState({
          open: true,
          message: "File size should not exceed 2MB.",
          severity: "error",
        });
        return;
      }
      const newFileName = file.name.replace(/\s+/g, "_");
      const renamedFile = new File([file], newFileName, {
        type: file.type,
      });
      let uploadPromises = [];
      uploadPromises.push(createBlobInContainer(renamedFile));

      if (uploadPromises.length > 0) {
        const uploadFiles = async () => {
          await Promise.all(uploadPromises);

          const containerName = process.env.REACT_APP_STORAGE_CONTAINER;
          const storageAccountName = process.env.REACT_APP_STORAGERESOURCENAME;
          const imageUrl = `https://${storageAccountName}.blob.core.windows.net/${containerName}/${renamedFile.name}`;
          setPrizeDrawBody({ ...prizeDrawBody, image_en: imageUrl });
        };

        uploadFiles().catch(console.error);
      }
    }
  };

  const handleSubmit = async () => {
    console.log("Submitting form...", prizeDrawBody);
    const missingFields = [];
    if (!prizeDrawBody.title_en?.trim()) 
      missingFields.push("Title (English)");
    if (!prizeDrawBody.startDate) 
      missingFields.push("Start Date");
    if (!prizeDrawBody.closeDate) 
      missingFields.push("Close Date");
    if (prizeDrawBody.maxTicket === "" || prizeDrawBody.maxTicket === null || prizeDrawBody.maxTicket === undefined) {
      missingFields.push("Max Ticket");
    }

    if (missingFields.length > 0) {
      setAlertState({
        open: true,
        message: `Please fill in required fields: ${missingFields.join(", ")}`,
        severity: "error",
      });
      return;
    }
    const startDate = new Date(prizeDrawBody.startDate);
    const endDate = new Date(prizeDrawBody.closeDate);
    const currentDate = new Date();

    if (startDate >= endDate) {
      setAlertState({
        open: true,
        message: "Submission end date must be after start date",
        severity: "error",
      });
      return;
    }

    if (prizeDrawParams) {
      await put(
        `/admin/raffles/${prizeDrawParams.raffleId}`,
        prizeDrawBody
      );
      window.location.href = "/dashboard/prize-draws";
    } else {
      if (endDate < currentDate) {
        setAlertState({
          open: true,
          message: "Draw date cannot be in the past",
          severity: "error",
        });
        return;
      }
      const response = await post(`/admin/raffles`, prizeDrawBody);
      if (response.success) {
        setAlertState({
          open: true,
          message: "Prize draw created successfully",
          severity: "success",
        });
        window.location.href = "/dashboard/prize-draws";
      }
    }
  };

  const handleChangeFeatured = (event) => {
    setPrizeDrawBody((prevState) => ({
      ...prevState,
      isFeatured: event.target.value,
    }));
  };

  const handlePrizeSubmit = async (data) => {
    try {
      setPrizeLoading(true);
      const idString = data.id.toString();
      const isNewAdded = idString.includes("temp-");
      if (!isNewAdded) {
        const resp = await put(`/admin/raffles/${prizeDrawParams.raffleId}/prize/${data.id}`, {
          title_en: data.title_en,
          title_fr: data.title_fr || "",
          description_en: data.description_en || "",
          description_fr: data.description_fr || "",
          tickets: data.tickets,
          prizeId: prizeDrawParams.prizeId,
        });
        if (resp.success) {
          setPrizeToggle(!prizeToggle);
        }
      } else {
        const resp = await post(`/admin/raffles/${prizeDrawParams.raffleId}/prize`, {
          title_en: data.title_en,
          title_fr: data.title_fr || "",
          description_en: data.description_en || "",
          description_fr: data.description_fr || "",
          tickets: data.tickets,
        });
        if (resp.success) {
          setPrizeToggle(!prizeToggle);
        }
      }
    } catch (error) {
      setPrizeLoading(false);
    } finally {
      setPrizeLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{`${prizeDrawParams ? "Update" : "Create"} Prize Draw`}</title>
      </Helmet>
      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4">{`${
            prizeDrawParams ? "Update" : "Create"
          } Prize Draw`}</Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() =>
                (window.location.href =
                  "/dashboard/prize-draws?tenant=football")
              }
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              startIcon={<Iconify icon="eva:save-fill" />}
              sx={{ mr: 1 }}
            >
              Save
            </Button>
          </Stack>
        </Stack>
        <PrizeDrawForm
          prizeDrawBody={prizeDrawBody}
          setPrizeDrawBody={setPrizeDrawBody}
          handleDateChange={handleDateChange}
          handleChangeFeatured={handleChangeFeatured}
          handleChangeStatus={handleChangeStatus}
          fileInputRef={fileInputRef}
          handleButtonClick={handleButtonClick}
          uploadFiles={uploadFiles}
          handlePrizeChange={handlePrizeChange}
          handlePrizeSubmit={handlePrizeSubmit}
          handleRemovePrizeRow={handleRemovePrizeRow}
          handleAddPrizeRow={handleAddPrizeRow}
          loadingDeletePrizeIds={loadingDeletePrizeIds}
          prizeDrawParams={prizeDrawParams}
          prizeLoading={prizeLoading}
          postalEntryRef={postalEntryRef}
          tAndCsRef={tAndCsRef}
          winnersMsgRef={winnersMsgRef}
        />
        <Snackbar
          open={alertState.open}
          autoHideDuration={4500}
          onClose={() =>
            setAlertState((prev) => ({
              ...prev,
              open: false,
            }))
          }
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={() =>
              setAlertState((prev) => ({
                ...prev,
                open: false,
              }))
            }
            severity={alertState.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {alertState.message}
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
}
